import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  createId,
  pruneUndefined,
  watchSummary,
  type AppSettings,
  type Copy,
  type DiscFormat,
  type Movie,
  type ParsedImport,
  type Watch,
  type WishlistItem,
} from '@physical-movies/core';
import { COLLECTIONS, SETTINGS_DOC, getDb } from '../firebase';
import type { ImportSummary, NewMovieInput, NewWishlistInput, WatchInput } from '../app/context';

/**
 * Every Firestore write in the app.
 *
 * Documents are plain JSON — ISO date strings, no Timestamps — so what you
 * export is exactly what is stored.
 */

const now = () => new Date().toISOString();

/** Firestore caps a batch at 500 operations. */
const BATCH_LIMIT = 400;

const moviesRef = () => collection(getDb(), COLLECTIONS.movies);
const watchesRef = () => collection(getDb(), COLLECTIONS.watches);
const wishlistRef = () => collection(getDb(), COLLECTIONS.wishlist);

const settingsDoc = () => doc(getDb(), COLLECTIONS.settings, SETTINGS_DOC);

const movieDoc = (id: string) => doc(getDb(), COLLECTIONS.movies, id);
const watchDoc = (id: string) => doc(getDb(), COLLECTIONS.watches, id);
const wishlistDoc = (id: string) => doc(getDb(), COLLECTIONS.wishlist, id);

// --- Settings ----------------------------------------------------------------

/**
 * The whole settings document, written at once.
 *
 * It is one small document that only ever changes by a deliberate tap, so
 * there is nothing to gain from patching individual fields — and writing it
 * whole means the shape on disk always matches the shape in the app.
 */
export async function saveSettings(settings: AppSettings): Promise<void> {
  await setDoc(settingsDoc(), settings);
}

// --- Movies ------------------------------------------------------------------

export async function createMovie(input: NewMovieInput): Promise<string> {
  const ref = doc(moviesRef());
  const stamp = now();

  const movie: Movie = {
    id: ref.id,
    tmdbId: input.tmdbId,
    title: input.title,
    titleDe: input.titleDe ?? null,
    originalTitle: input.originalTitle ?? null,
    year: input.year,
    releaseDate: input.releaseDate ?? null,
    runtime: input.runtime,
    posterPath: input.posterPath ?? null,
    backdropPath: input.backdropPath ?? null,
    overviewEn: input.overviewEn ?? null,
    overviewDe: input.overviewDe ?? null,
    genreIds: input.genreIds ?? [],
    directors: input.directors ?? [],
    copies: input.copies,
    personalNote: null,
    addedAt: stamp,
    updatedAt: stamp,
    watchCount: 0,
    lastWatchedAt: null,
  };

  const batch = writeBatch(getDb());
  batch.set(ref, pruneUndefined(movie));
  await batch.commit();
  return ref.id;
}

export async function updateMovie(id: string, patch: Partial<Movie>): Promise<void> {
  await updateDoc(movieDoc(id), pruneUndefined({ ...patch, updatedAt: now() }));
}

export async function setCopies(movieId: string, copies: Copy[]): Promise<void> {
  await updateMovie(movieId, { copies });
}

/** Removes the movie and every watch that pointed at it. */
export async function deleteMovie(movieId: string): Promise<void> {
  const owned = await getDocs(query(watchesRef(), where('movieId', '==', movieId)));

  const batch = writeBatch(getDb());
  batch.delete(movieDoc(movieId));
  for (const entry of owned.docs) batch.delete(entry.ref);
  await batch.commit();
}

// --- Watches -----------------------------------------------------------------

/**
 * Writes the watch and the movie's denormalised summary in one atomic batch,
 * so the grid never shows a count that disagrees with the history list.
 */
export async function logWatch(movie: Movie, input: WatchInput): Promise<void> {
  const ref = doc(watchesRef());
  const stamp = now();

  const watch: Watch = {
    id: ref.id,
    movieId: movie.id,
    watchedAt: input.watchedAt,
    rating: input.rating ?? null,
    note: input.note ?? null,
    // Snapshot the runtime so a later TMDB correction cannot rewrite history.
    runtimeMinutes: movie.runtime,
    createdAt: stamp,
  };

  const latest =
    movie.lastWatchedAt && movie.lastWatchedAt > watch.watchedAt
      ? movie.lastWatchedAt
      : watch.watchedAt;

  const batch = writeBatch(getDb());
  batch.set(ref, pruneUndefined(watch));
  batch.update(movieDoc(movie.id), {
    watchCount: movie.watchCount + 1,
    lastWatchedAt: latest,
    updatedAt: stamp,
  });
  await batch.commit();
}

export async function updateWatch(
  watch: Watch,
  patch: Partial<Watch>,
  siblings: Watch[],
): Promise<void> {
  const updated = { ...watch, ...patch };
  const summary = watchSummary(siblings.map((entry) => (entry.id === watch.id ? updated : entry)));

  const batch = writeBatch(getDb());
  batch.update(watchDoc(watch.id), pruneUndefined(patch));
  batch.update(movieDoc(watch.movieId), { ...summary, updatedAt: now() });
  await batch.commit();
}

export async function deleteWatch(watch: Watch, siblings: Watch[]): Promise<void> {
  const summary = watchSummary(siblings.filter((entry) => entry.id !== watch.id));

  const batch = writeBatch(getDb());
  batch.delete(watchDoc(watch.id));
  batch.update(movieDoc(watch.movieId), { ...summary, updatedAt: now() });
  await batch.commit();
}

// --- Wishlist ----------------------------------------------------------------

export async function addWishlistItem(item: NewWishlistInput): Promise<void> {
  const ref = doc(wishlistRef());
  const entry: WishlistItem = {
    ...item,
    genreIds: item.genreIds ?? [],
    buyable: item.buyable ?? true,
    deletedAt: null,
    id: ref.id,
    addedAt: now(),
  };
  const batch = writeBatch(getDb());
  batch.set(ref, pruneUndefined(entry));
  await batch.commit();
}

export async function updateWishlistItem(id: string, patch: Partial<WishlistItem>): Promise<void> {
  await updateDoc(wishlistDoc(id), pruneUndefined(patch));
}

/**
 * Moves an entry to the trash rather than removing it.
 *
 * A wishlist is built up over months and a mis-tap on a phone is easy, so the
 * entry stays readable for TRASH_RETENTION_DAYS and only then goes for good.
 */
export async function trashWishlistItem(id: string): Promise<void> {
  await updateDoc(wishlistDoc(id), { deletedAt: now() });
}

export async function restoreWishlistItem(id: string): Promise<void> {
  await updateDoc(wishlistDoc(id), { deletedAt: null });
}

export async function deleteWishlistItem(id: string): Promise<void> {
  await deleteDoc(wishlistDoc(id));
}

/**
 * Clear out entries whose retention has run out.
 *
 * The free plan has no server to run a scheduled job on, so this happens
 * opportunistically when the app loads. Failures are swallowed on purpose: an
 * unpurged entry is untidy, not broken, and must never stop the app starting.
 */
export async function purgeExpiredWishlistItems(ids: string[]): Promise<number> {
  if (ids.length === 0) return 0;
  const doomed = ids.slice(0, BATCH_LIMIT);
  try {
    const batch = writeBatch(getDb());
    for (const id of doomed) batch.delete(wishlistDoc(id));
    await batch.commit();
    return doomed.length;
  } catch (error) {
    console.error('Could not purge expired wishlist entries', error);
    return 0;
  }
}

/** "I bought it": the wishlist entry becomes a movie with its first copy. */
export async function moveWishlistItemToCollection(
  item: WishlistItem,
  format: DiscFormat,
): Promise<string> {
  const movieRef = doc(moviesRef());
  const stamp = now();

  const movie: Movie = {
    id: movieRef.id,
    tmdbId: item.tmdbId,
    title: item.title,
    titleDe: item.titleDe ?? null,
    originalTitle: null,
    year: item.year,
    releaseDate: item.releaseDate ?? null,
    runtime: item.runtime,
    posterPath: item.posterPath,
    backdropPath: null,
    overviewEn: null,
    overviewDe: null,
    genreIds: item.genreIds ?? [],
    directors: [],
    copies: [{ id: createId(), format, edition: null, notes: null, purchasedAt: null, loan: null }],
    personalNote: item.note ?? null,
    addedAt: stamp,
    updatedAt: stamp,
    watchCount: 0,
    lastWatchedAt: null,
  };

  const batch = writeBatch(getDb());
  batch.set(movieRef, pruneUndefined(movie));
  batch.delete(wishlistDoc(item.id));
  await batch.commit();

  return movieRef.id;
}

// --- Import ------------------------------------------------------------------

/**
 * Merge an export into this collection.
 *
 * Entries keep their ids, so importing the same file twice updates rather than
 * duplicates. Watches pointing at a movie that is neither in the file nor
 * already on the shelf are dropped — an orphan watch would quietly skew every
 * statistic.
 */
export async function importCollection(
  parsed: ParsedImport,
  existingMovieIds: Set<string>,
): Promise<ImportSummary> {
  const knownIds = new Set([...existingMovieIds, ...parsed.movies.map((movie) => movie.id)]);
  const watches = parsed.watches.filter((watch) => knownIds.has(watch.movieId));

  const writes: { ref: ReturnType<typeof movieDoc>; data: unknown }[] = [
    ...parsed.movies.map((movie) => ({ ref: movieDoc(movie.id), data: pruneUndefined(movie) })),
    ...watches.map((watch) => ({ ref: watchDoc(watch.id), data: pruneUndefined(watch) })),
    ...parsed.wishlist.map((item) => ({ ref: wishlistDoc(item.id), data: pruneUndefined(item) })),
  ];

  for (let index = 0; index < writes.length; index += BATCH_LIMIT) {
    const batch = writeBatch(getDb());
    for (const write of writes.slice(index, index + BATCH_LIMIT)) {
      batch.set(write.ref, write.data as Record<string, unknown>, { merge: true });
    }
    await batch.commit();
  }

  // Imported movies carry their own watchCount, but a merge can add watches to
  // a movie that was already here, so recompute every affected summary.
  const affected = new Set(watches.map((watch) => watch.movieId));
  for (const movieId of affected) {
    const owned = await getDocs(query(watchesRef(), where('movieId', '==', movieId)));
    const list = owned.docs.map((entry) => entry.data() as Watch);
    await updateDoc(movieDoc(movieId), { ...watchSummary(list), updatedAt: now() });
  }

  return {
    movies: parsed.movies.length,
    watches: watches.length,
    wishlist: parsed.wishlist.length,
  };
}
